import type { AgentDocSkill, AgentFileKind } from '../agent-code.js';
import type { Actionable } from './actions.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DOSYA KARTI — AI'ın ürettiği dosya (Faz 9 X1, 1 Eki 2026)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Kod çalıştırma / beceri (Excel, PowerPoint, Word, PDF, grafik, CSV) bir
 * dosya ürettiğinde sunucu onu sağlayıcıdan (Files API) alıp R2'ye yazıyor,
 * sağlayıcıdaki kopyayı siliyor ve asistan satırına bu kartı ekliyor. Kart
 * bir ARAÇ sonucu değil (model dosyayı `$OUTPUT_DIR`e koydu, kimlik görmedi)
 * — sunucunun eklediği bir kayıt (`toolCallId: file_<kimlik>`).
 *
 * İlkeler:
 *   · 🔴 Kart yalnız KİMLİK + META taşır (ad, tür, boyut). Dosya İÇERİĞİ
 *     kişisel veri taşıyabilir — karta, sohbete, günlüğe girmez.
 *   · 🔴 SÜRELİ BAĞLANTI KARTA YAZILMAZ: indirme (ve görsel önizleme) adresi
 *     her çizimde TAZE, rol denetimli canlı uçtan (`GET /agent/cards/:id/data`).
 *   · Gösterim sınıfı `always` — kullanıcı dosyayı istedi; kart işin kendisi.
 *   · "Yeniden üret" bir `reply` eylemi: düğme hiçbir şey yürütmüyor, modele
 *     `[Card action … ("regenerate")]` satırı gidiyor ve dosyayı baştan kuruyor.
 */
export type FilesAgentCard = Actionable<{
  kind: 'file';
  /** Bizim kimliğimiz (uuid) — R2 anahtarı sunucuda bundan türetiliyor. */
  fileId: string;
  /** Görünen ad (temizlenmiş; uzantılı). */
  name: string;
  /** Küçük harf, noktasız uzantı (`xlsx`). */
  ext: string;
  mime: string;
  sizeBytes: number | null;
  fileKind: AgentFileKind;
  /** Biliniyorsa üreten beceri (rozet). */
  skill: AgentDocSkill | null;
  /** Ekranda önizlenebilir mi (yalnız raster görsel). */
  previewable: boolean;
  /** `failed` — sağlayıcıdan alınamadı / depoya yazılamadı / boyut tavanı. */
  status: 'ready' | 'failed';
  /** Başarısızlık kodu — ekran `cards.file.error.<kod>` ile çevirir. */
  code: 'too_large' | 'download_failed' | 'storage_failed' | null;
  createdAt: string;
}>;

export const FILES_CARD_KINDS = ['file'] as const satisfies readonly FilesAgentCard['kind'][];

/** Araç adıyla çizilen kart yok — kart sunucunun eklediği kayıt. */
export const FILES_TOOL_CARDS: Readonly<Record<string, readonly FilesAgentCard['kind'][]>> = {};

/** Kartın satırına yazılan sentetik araç adı (`toolCalls[].name`). */
export const FILE_OUTPUT_TOOL_NAME = 'file_output' as const;

/** Sentetik kart kimliği — `GET /agent/cards/:callId/data` deseniyle uyumlu. */
export function fileOutputCallId(fileId: string): string {
  return `file_${fileId.replace(/[^A-Za-z0-9_-]/g, '')}`;
}

export interface FilesCardLiveData {
  file: {
    /** Dosya hâlâ depoda mı (sohbet silinmediyse evet). */
    available: boolean;
    /** İmzalı, süreli — yalnız bellekte; karta/sohbete yazılmaz. */
    downloadUrl: string | null;
    /** Yalnız raster görselde; süreli. */
    previewUrl: string | null;
    expiresAt: string | null;
  };
}
